import type { WarningType, Lane, LaneAvailability, Treatment, CalendarView } from './types';
import {
  localTimeToIso,
  extractWallParts,
  toMinutesFromMidnight,
  formatTime,
  localMinutesToIso,
  formatDateInTz,
} from '@/lib/utils/datetime-tz';

// --- Shared Constants ---

/** Pixels-per-minute lookup keyed by TimeJump. Shared across day view, week view, and drag handlers. */
export const PIXELS_PER_MINUTE_BY_JUMP: Record<number, number> = {
  15: 2.0,
  30: 1.5,
  60: 1.0,
  120: 0.6,
  240: 0.4,
};

// --- Treatment Matching ---

/**
 * Find the first treatment from `requestTreatmentIds` that is compatible with `lane`.
 * - If `requestTreatmentIds` is empty → returns `null` (any lane is compatible; caller uses default duration).
 * - A treatment matches if the lane's `treatmentIds` is empty (universal) or includes the treatment ID.
 * - Returns `null` when no treatment matches (incompatible lane).
 */
export function findMatchingTreatment(
  requestTreatmentIds: string[],
  lane: Lane,
  treatments: Treatment[],
): Treatment | null {
  if (requestTreatmentIds.length === 0) return null;
  const laneTreatmentIds = lane.treatmentIds ?? [];
  for (const id of requestTreatmentIds) {
    if (laneTreatmentIds.length === 0 || laneTreatmentIds.includes(id)) {
      const t = treatments.find((tr) => tr.treatmentId === id);
      if (t) return t;
    }
  }
  return null;
}

/**
 * Whether a request may fill an empty slot: the slot has no treatment, the
 * request has no treatment preference, or the slot's treatment is among the
 * request's. Mirrors findMatchingTreatment's "empty means universal" convention.
 */
export function isRequestCompatibleWithSlot(
  requestTreatmentIds: string[],
  slotTreatmentId: string | undefined,
): boolean {
  return (
    !slotTreatmentId ||
    requestTreatmentIds.length === 0 ||
    requestTreatmentIds.includes(slotTreatmentId)
  );
}

// --- Time Utilities ---
// Re-exported from the shared datetime-tz module so non-slotting callers can
// use the same helpers via @/lib/utils/datetime-tz.
export {
  toMinutesFromMidnight,
  formatTime,
  localMinutesToIso,
} from '@/lib/utils/datetime-tz';
export { localTimeToIso };

/**
 * Generate one daily LaneAvailability window for each calendar day between
 * `offeringStart` and `offeringEnd` (inclusive). Each window spans `startTime`
 * to `endTime` interpreted in `timezone` and stored as a UTC ISO string.
 * Shared by the offering-create wizard (where no offering record exists yet)
 * and the post-create lane popover.
 */
export function generateDailyAvailability(
  offeringStart: string,
  offeringEnd: string,
  startTime: string,
  endTime: string,
  timezone: string,
): LaneAvailability[] {
  // Anchor day iteration in the target timezone so cross-DST and far-from-UTC
  // browsers don't duplicate or skip days. UTC.Date arithmetic on the wall-day
  // is linear and avoids local-DST math; localTimeToIso handles the time math.
  const startParts = extractWallParts(offeringStart, timezone);
  const endParts = extractWallParts(offeringEnd, timezone);

  const cursor = new Date(Date.UTC(startParts.year, startParts.month, startParts.day));
  const endAnchor = new Date(Date.UTC(endParts.year, endParts.month, endParts.day));

  const result: LaneAvailability[] = [];
  while (cursor.getTime() <= endAnchor.getTime()) {
    const y = cursor.getUTCFullYear();
    const m = String(cursor.getUTCMonth() + 1).padStart(2, '0');
    const d = String(cursor.getUTCDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    result.push({
      startDt: localTimeToIso(dateStr, startTime, timezone),
      endDt: localTimeToIso(dateStr, endTime, timezone),
    });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return result;
}

/**
 * Move every availability window that falls on `fromDate` to `toDate`, keeping
 * each window's wall-clock time intact across the date change (DST-safe via the
 * minutes-from-midnight round-trip). Windows on other days are left untouched.
 *
 * Day matching uses the tz-LOCAL wall date (the same key `groupByDay` uses in
 * the editor), so the moved windows regroup under the new day. A raw
 * `startDt` UTC prefix would mis-match every window whose UTC date differs from
 * its event-tz wall date (evening slots in +tz, morning slots in −tz). If
 * `toDate` already has windows the two sets merge; the result is sorted
 * chronologically so the day blocks stay in order.
 */
export function moveDayAvailability(
  windows: LaneAvailability[],
  fromDate: string,
  toDate: string,
  timezone: string,
): LaneAvailability[] {
  if (fromDate === toDate) return windows;
  const moved = windows.map((w) => {
    if (formatDateInTz(w.startDt, "en-CA", timezone) !== fromDate) return w;
    const startT = formatTime(toMinutesFromMidnight(w.startDt, timezone));
    const endT = formatTime(toMinutesFromMidnight(w.endDt, timezone));
    return {
      ...w,
      startDt: localTimeToIso(toDate, startT, timezone),
      endDt: localTimeToIso(toDate, endT, timezone),
    };
  });
  return [...moved].sort((a, b) => a.startDt.localeCompare(b.startDt));
}

/**
 * Clone every availability window on `fromDate` onto `toDate`, preserving each
 * window's wall-clock time (DST-safe). Originals are
 * kept; the clones are appended and the whole list re-sorted chronologically so
 * the day blocks stay in order. If `toDate` already has windows the clones merge
 * with them (no dedup — mirrors the "add slot" behaviour).
 */
export function duplicateDayAvailability(
  windows: LaneAvailability[],
  fromDate: string,
  toDate: string,
  timezone: string,
): LaneAvailability[] {
  const clones = windows
    .filter((w) => formatDateInTz(w.startDt, "en-CA", timezone) === fromDate)
    .map((w) => {
      const startT = formatTime(toMinutesFromMidnight(w.startDt, timezone));
      const endT = formatTime(toMinutesFromMidnight(w.endDt, timezone));
      return {
        startDt: localTimeToIso(toDate, startT, timezone),
        endDt: localTimeToIso(toDate, endT, timezone),
      };
    });
  return [...windows, ...clones].sort((a, b) =>
    a.startDt.localeCompare(b.startDt),
  );
}

/**
 * Date keys (`YYYY-MM-DD`) on which two or more availability windows overlap.
 *
 * Windows are bucketed by their tz-LOCAL wall date (same key the editor groups
 * by), sorted, and a date is flagged when any window starts before the previous
 * one ends. Drives the editor's per-day overlap warning badge.
 */
export function overlappingDayKeys(
  windows: LaneAvailability[],
  timezone: string,
): Set<string> {
  const byDay = new Map<string, LaneAvailability[]>();
  for (const w of windows) {
    const key = formatDateInTz(w.startDt, "en-CA", timezone);
    const bucket = byDay.get(key) ?? [];
    bucket.push(w);
    byDay.set(key, bucket);
  }
  const result = new Set<string>();
  for (const [key, bucket] of byDay) {
    const sorted = [...bucket].sort(
      (a, b) => new Date(a.startDt).getTime() - new Date(b.startDt).getTime(),
    );
    for (let i = 1; i < sorted.length; i++) {
      if (
        new Date(sorted[i].startDt).getTime() <
        new Date(sorted[i - 1].endDt).getTime()
      ) {
        result.add(key);
        break;
      }
    }
  }
  return result;
}

/**
 * True when availability is not saveable: any window with `start >= end` (range
 * check, applied to ALL windows incl. blocked) OR any non-blocked overlap.
 * Mirrors what the backend `validateLane` rejects, so the editor disables Save
 * before a doomed request is sent.
 */
export function hasAvailabilityIssue(
  windows: LaneAvailability[],
  timezone: string,
): boolean {
  for (const w of windows) {
    if (
      new Date(w.startDt).getTime() >= new Date(w.endDt).getTime()
    ) {
      return true;
    }
  }
  return overlappingDayKeys(windows, timezone).size > 0;
}

// --- View date ranges (shared by toolbar navigation and post-commit refresh) ---

export function getWeekRange(dateStr: string): { start: Date; end: Date } {
  const d = new Date(dateStr + "T12:00:00");
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((day + 6) % 7));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: monday, end: sunday };
}

/**
 * Fetch range (UTC ISO bounds) covering the whole window a calendar view shows.
 * Bounds are anchored at LOCAL midnight in the offering timezone, not UTC
 * midnight — otherwise, for any non-UTC offering, the fetched set is shifted by
 * the tz offset and the calendar (which renders in the offering tz) shows the
 * wrong slice of the day. Pass the offering tz (offering.timezone ??
 * eventTimezone ?? "UTC"); defaults to "UTC" for the degenerate case.
 */
export function getDateRange(
  view: CalendarView,
  dateStr: string,
  tz: string = "UTC",
): { startDt: string; endDt: string } {
  if (view === "week") {
    const { start, end } = getWeekRange(dateStr);
    return {
      startDt: localMinutesToIso(start.toISOString().split("T")[0], 0, tz),
      endDt: localMinutesToIso(end.toISOString().split("T")[0], 24 * 60, tz),
    };
  }
  if (view === "month") {
    const d = new Date(dateStr + "T12:00:00");
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return {
      startDt: localMinutesToIso(first.toISOString().split("T")[0], 0, tz),
      endDt: localMinutesToIso(last.toISOString().split("T")[0], 24 * 60, tz),
    };
  }
  return {
    startDt: localMinutesToIso(dateStr, 0, tz),
    endDt: localMinutesToIso(dateStr, 24 * 60, tz),
  };
}

/**
 * tz-anchored [startIso, endIso) bounds for a calendar day. Single source of
 * truth for "which appointments belong to this day" — used by the day-view grid
 * grouping and the day-scoped bulk-select set so they cannot drift apart.
 */
export function dayBoundsIso(dateStr: string, tz: string): { dayStart: string; dayEnd: string } {
  return {
    dayStart: localMinutesToIso(dateStr, 0, tz),
    dayEnd: localMinutesToIso(dateStr, 24 * 60, tz),
  };
}

/** Whether an appointment overlaps the given tz-anchored day window. */
export function appointmentOnDay(
  appt: { startDt: string; endDt: string },
  bounds: { dayStart: string; dayEnd: string },
): boolean {
  return appt.startDt < bounds.dayEnd && appt.endDt > bounds.dayStart;
}

/**
 * The date the toolbar's "today" button should jump to: today, but clamped to
 * the offering's [start, end] window. When today falls before the offering
 * starts (e.g. an event being planned months ahead) the literal today has no
 * availability, so we return the first offering day instead; symmetrically we
 * return the last day when today is past the window. `position` lets the UI
 * relabel the button ("Start"/"End") when it isn't actually jumping to today.
 *
 * Uses UTC date strings to match the calendar grid's own `isToday` detection.
 */
export function clampTodayToOffering(
  offeringStartDt: string | null,
  offeringEndDt: string | null,
): { date: string; position: 'before' | 'within' | 'after' } {
  const today = new Date().toISOString().split("T")[0];
  // Dateless offering has no window to clamp into — land on today.
  if (!offeringStartDt || !offeringEndDt) return { date: today, position: 'within' };
  const start = offeringStartDt.split("T")[0];
  const end = offeringEndDt.split("T")[0];
  if (today < start) return { date: start, position: 'before' };
  if (today > end) return { date: end, position: 'after' };
  return { date: today, position: 'within' };
}

// --- Offering range enumeration (toolbar date dropdown) ---

/** Defensive ceiling against corrupt multi-decade offering ranges. */
const MAX_ENUMERATED_ENTRIES = 750;

const utcCursorToDateStr = (d: Date): string => {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Wall-day anchors for an offering window, using the same tz-anchored UTC-cursor
 * idiom as `generateDailyAvailability` so cross-DST and far-from-UTC browsers
 * don't duplicate or skip days. Returns null for inverted (corrupt) ranges.
 */
function offeringWallAnchors(
  offeringStartDt: string,
  offeringEndDt: string,
  tz: string,
): { cursor: Date; endAnchor: Date; startDateStr: string } | null {
  const startParts = extractWallParts(offeringStartDt, tz);
  const endParts = extractWallParts(offeringEndDt, tz);
  const cursor = new Date(Date.UTC(startParts.year, startParts.month, startParts.day));
  const endAnchor = new Date(Date.UTC(endParts.year, endParts.month, endParts.day));
  if (endAnchor.getTime() < cursor.getTime()) return null;
  return { cursor, endAnchor, startDateStr: utcCursorToDateStr(cursor) };
}

export type OfferingDayOption = {
  /** YYYY-MM-DD wall date in the offering tz. */
  date: string;
  /** 1-based day number within the offering ("Tag 1"). */
  index: number;
};

/** Every calendar day of the offering window, in offering-tz wall dates. */
export function enumerateOfferingDays(
  offeringStartDt: string,
  offeringEndDt: string,
  tz: string,
): OfferingDayOption[] {
  const anchors = offeringWallAnchors(offeringStartDt, offeringEndDt, tz);
  if (!anchors) return [];
  const { cursor, endAnchor } = anchors;

  const result: OfferingDayOption[] = [];
  while (cursor.getTime() <= endAnchor.getTime() && result.length < MAX_ENUMERATED_ENTRIES) {
    result.push({ date: utcCursorToDateStr(cursor), index: result.length + 1 });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return result;
}

export type OfferingWeekOption = {
  /** Jump target: the week's Monday, clamped to the offering start. */
  date: string;
  /** Monday of the week (YYYY-MM-DD), for labeling. */
  weekStart: string;
  /** Sunday of the week (YYYY-MM-DD), for labeling. */
  weekEnd: string;
};

/**
 * Mon–Sun week buckets covering the offering window (consistent with
 * `getWeekRange`). The first bucket's jump target is clamped to the offering
 * start so `currentDate` never lands before the offering.
 */
export function enumerateOfferingWeeks(
  offeringStartDt: string,
  offeringEndDt: string,
  tz: string,
): OfferingWeekOption[] {
  const anchors = offeringWallAnchors(offeringStartDt, offeringEndDt, tz);
  if (!anchors) return [];
  const { cursor, endAnchor, startDateStr } = anchors;

  const monday = new Date(cursor);
  monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));

  const result: OfferingWeekOption[] = [];
  while (monday.getTime() <= endAnchor.getTime() && result.length < MAX_ENUMERATED_ENTRIES) {
    const sunday = new Date(monday);
    sunday.setUTCDate(sunday.getUTCDate() + 6);
    const weekStart = utcCursorToDateStr(monday);
    result.push({
      date: weekStart < startDateStr ? startDateStr : weekStart,
      weekStart,
      weekEnd: utcCursorToDateStr(sunday),
    });
    monday.setUTCDate(monday.getUTCDate() + 7);
  }
  return result;
}

export type OfferingMonthOption = {
  /** Jump target: first of the month, clamped to the offering start. */
  date: string;
};

/** Calendar months covering the offering window (handles year rollover). */
export function enumerateOfferingMonths(
  offeringStartDt: string,
  offeringEndDt: string,
  tz: string,
): OfferingMonthOption[] {
  const anchors = offeringWallAnchors(offeringStartDt, offeringEndDt, tz);
  if (!anchors) return [];
  const { cursor, endAnchor, startDateStr } = anchors;

  const monthCursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), 1));
  const result: OfferingMonthOption[] = [];
  while (monthCursor.getTime() <= endAnchor.getTime() && result.length < MAX_ENUMERATED_ENTRIES) {
    const first = utcCursorToDateStr(monthCursor);
    result.push({ date: first < startDateStr ? startDateStr : first });
    monthCursor.setUTCMonth(monthCursor.getUTCMonth() + 1);
  }
  return result;
}

/** Compact duration label from a minute count: "2h 30m" / "45m" / "0m". */
export function formatMinutes(totalMinutes: number): string {
  const m = Math.max(0, Math.round(totalMinutes));
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h === 0) return `${min}m`;
  if (min === 0) return `${h}h`;
  return `${h}h ${min}m`;
}

export function calculateTimeBlockPosition(
  startMinutes: number,
  endMinutes: number,
  pixelsPerMinute: number
): { top: number; height: number } {
  return {
    top: startMinutes * pixelsPerMinute,
    height: Math.max((endMinutes - startMinutes) * pixelsPerMinute, 4),
  };
}

export { formatDate } from '@/lib/utils/datetime-tz';

// --- Color Management ---

export const USER_COLORS = {
  bg: [
    'lane-blue-light', 'lane-red-light', 'lane-green-light', 'lane-yellow-light', 'lane-purple-light',
    'lane-pink-light', 'lane-indigo-light', 'lane-orange-light', 'lane-teal-light', 'lane-cyan-light',
  ],
  border: [
    'outline-lane-blue', 'outline-lane-red', 'outline-lane-green', 'outline-lane-yellow', 'outline-lane-purple',
    'outline-lane-pink', 'outline-lane-indigo', 'outline-lane-orange', 'outline-lane-teal', 'outline-lane-cyan',
  ],
  dot: [
    'lane-blue-dark', 'lane-red-dark', 'lane-green-dark', 'lane-yellow-dark', 'lane-purple-dark',
    'lane-pink-dark', 'lane-indigo-dark', 'lane-orange-dark', 'lane-teal-dark', 'lane-cyan-dark',
  ],
} as const;

export function getColorForIndex(index: number) {
  const i = index % USER_COLORS.bg.length;
  return {
    bg: USER_COLORS.bg[i],
    border: USER_COLORS.border[i],
    dot: USER_COLORS.dot[i],
  };
}

// --- Warning Utilities ---

export function getWarningSeverity(type: WarningType): 'info' | 'warning' | 'error' {
  switch (type) {
    case 'BELOW_THRESHOLD': return 'info';
    case 'OUTSIDE_AVAILABILITY':
    case 'BUFFER_TIME_VIOLATION':
    case 'CROSS_OFFERING_CONFLICT':
    case 'MAX_BOOKINGS_WARNING': return 'warning';
    case 'CAPACITY_EXCEEDED':
    case 'MISSING_TREATMENT': return 'error';
  }
}

// --- Warning Message Translation ---

const WARNING_PARSERS: Record<string, (msg: string) => Record<string, string | number> | null> = {
  MAX_BOOKINGS_WARNING: (msg) => {
    const m = msg.match(/(\d+)\s*bookings?\s*\(max:\s*(\d+)\)/i);
    return m ? { current: Number(m[1]), maxAllowed: Number(m[2]) } : null;
  },
  CAPACITY_EXCEEDED: (msg) => {
    const m = msg.match(/(\d+)\/(\d+)\s*guests?/i);
    return m ? { used: Number(m[1]), total: Number(m[2]) } : null;
  },
  BUFFER_TIME_VIOLATION: (msg) => {
    const m = msg.match(/(\d+)\s*min/i);
    return m ? { minutes: Number(m[1]) } : null;
  },
  BELOW_THRESHOLD: (msg) => {
    const m = msg.match(/(\d+)\/(\d+)\s*guests?/i);
    return m ? { threshold: Number(m[2]) } : null;
  },
};

const WARNING_KEYS: Record<string, string> = {
  MAX_BOOKINGS_WARNING: 'warningMaxBookings',
  CAPACITY_EXCEEDED: 'warningCapacityExceeded',
  BUFFER_TIME_VIOLATION: 'warningBufferViolation',
  OUTSIDE_AVAILABILITY: 'warningOutsideAvailability',
  MISSING_TREATMENT: 'warningMissingTreatment',
  CROSS_OFFERING_CONFLICT: 'warningCrossOfferingConflict',
  BELOW_THRESHOLD: 'warningBelowThreshold',
};

// Param-less label variants, used as a fallback when the detailed message has
// `{placeholders}` but we couldn't parse the numbers out of the backend string —
// rendering the templated key with no params would leak literal `{used}/{total}`
// into the UI, so fall back to the clean label instead.
const WARNING_LABEL_KEYS: Record<string, string> = {
  MAX_BOOKINGS_WARNING: 'warningLabelMaxBookings',
  CAPACITY_EXCEEDED: 'warningLabelCapacityExceeded',
  BUFFER_TIME_VIOLATION: 'warningLabelBufferViolation',
  OUTSIDE_AVAILABILITY: 'warningLabelOutsideAvailability',
  MISSING_TREATMENT: 'warningLabelMissingTreatment',
  CROSS_OFFERING_CONFLICT: 'warningLabelCrossOfferingConflict',
  BELOW_THRESHOLD: 'warningLabelBelowThreshold',
};

export function getTranslatedWarningMessage(
  type: WarningType,
  backendMessage: string,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const key = WARNING_KEYS[type];
  if (!key) return backendMessage;

  const parser = WARNING_PARSERS[type];
  if (parser) {
    const params = parser(backendMessage);
    // Parser couldn't extract the placeholders' values → fall back to the
    // param-less label so the UI never shows raw `{used}/{total}` tokens.
    if (!params) return t(WARNING_LABEL_KEYS[type] ?? key);
    return t(key, params);
  }
  return t(key);
}

// --- Status Translation ---

const STATUS_KEYS: Record<string, string> = {
  TENTATIVE: 'statusTentative',
  CONFIRMED: 'statusConfirmed',
  CHECKED_IN: 'statusCheckedIn',
  CANCELLED: 'statusCancelled',
  WAITING: 'waitlistWaiting',
  PROMOTED: 'waitlistPromoted',
  EXPIRED: 'waitlistExpired',
};

export function statusTranslationKey(status: string): string {
  return STATUS_KEYS[status] ?? status;
}
